import Swiper from "./swiper-bundle.esm.browser.min.js";

const MAX_AUTO_SLIDE_SECONDS = 30;
const MIN_AUTO_SLIDE_SECONDS = 1;

class HomeBannerCarousel extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._initialized = false;
    this._resizeTimer = null;
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
    this._prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    document.addEventListener("visibilitychange", this._handleVisibilityChange);
    this._initSwiper();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    document.removeEventListener("visibilitychange", this._handleVisibilityChange);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._initSwiper();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    const legacyAutoplay =
      this.dataset.enableAutoplay === "true" ||
      this.dataset.enableAutoSlide === "true";
    const rawSeconds = parseInt(
      this.dataset.autoSlideSeconds ||
        this.dataset.autoplaySpeed ||
        "5",
      10,
    );
    const autoSlideSeconds = Math.min(
      Math.max(rawSeconds, MIN_AUTO_SLIDE_SECONDS),
      MAX_AUTO_SLIDE_SECONDS,
    );

    return {
      slideCount,
      showArrows: this.dataset.showArrows === "true" && slideCount > 1,
      showPagination: this.dataset.showPagination === "true" && slideCount > 1,
      enableAutoSlide: legacyAutoplay && slideCount > 1,
      autoSlideDelay: autoSlideSeconds * 1000,
      enableLoop: this.dataset.enableLoop === "true" && slideCount > 1,
      pauseOnHover: this.dataset.pauseOnHover !== "false",
      pauseOnInteraction: this.dataset.pauseOnInteraction === "true",
      sectionId: this.dataset.sectionId || "",
    };
  }

  _cleanupSwiperClasses() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slides = this.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "home-banner-carousel__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _syncSlideAccessibility(activeIndex) {
    const slides = this.querySelectorAll("[data-swiper-slide]");
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      slide.setAttribute("tabindex", isActive ? "0" : "-1");
    });
  }

  _initSwiper() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return;

    this._destroySwiper();

    swiperEl.classList.add("swiper", "home-banner-carousel__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    const config = this._readConfig();
    const canNavigate = slideEls.length > 1;

    const swiperConfig = {
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      centeredSlides: false,
      initialSlide: 0,
      watchOverflow: true,
      autoHeight: false,
      speed: 450,
      grabCursor: canNavigate,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous banner slide",
        nextSlideMessage: "Next banner slide",
      },
    };

    if (config.enableLoop && canNavigate) {
      swiperConfig.loop = true;
    }

    if (config.showArrows) {
      const nextEl = this.querySelector(
        `.home-banner-carousel__nav-next--${config.sectionId}`,
      );
      const prevEl = this.querySelector(
        `.home-banner-carousel__nav-prev--${config.sectionId}`,
      );
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination) {
      const paginationEl = this.querySelector(
        `.home-banner-carousel__pagination--${config.sectionId}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.slideCount;
            return `<button type="button" class="${className}" aria-label="Go to banner slide ${num} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoSlide) {
      const reduceMotion = this._prefersReducedMotion?.matches;
      if (!reduceMotion) {
        swiperConfig.autoplay = {
          delay: config.autoSlideDelay,
          disableOnInteraction: config.pauseOnInteraction,
          pauseOnMouseEnter: config.pauseOnHover,
        };
      }
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        const activeIndex = swiper.realIndex ?? swiper.activeIndex ?? 0;
        if (!config.enableLoop && activeIndex !== 0) {
          swiper.slideTo(0, 0);
        }
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex ?? 0);
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
    };

    this._swiper = new Swiper(swiperEl, swiperConfig);
    requestAnimationFrame(() => {
      this._swiper?.update();
      this._syncSlideAccessibility(this._swiper?.realIndex ?? this._swiper?.activeIndex ?? 0);
    });
  }

  _handleVisibilityChange() {
    if (!this._swiper || this._swiper.destroyed || !this._swiper.autoplay) return;
    if (document.hidden) {
      this._swiper.autoplay.stop();
    } else if (!this._prefersReducedMotion?.matches) {
      this._swiper.autoplay.start();
    }
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._cleanupSwiperClasses();
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      if (this._swiper && !this._swiper.destroyed) {
        this._swiper.update();
      }
    }, 150);
  }
}

if (!customElements.get("home-banner-carousel")) {
  customElements.define("home-banner-carousel", HomeBannerCarousel);
}
